<template>
  <div>
    <div class="d-flex mt-1 mb-3">
      <v-btn
        small
        elevation="2"
        width="5%"
        height="54px"
        class="mr-3"
        @click="isDetailsOpened = !isDetailsOpened"
      >
        <v-icon>{{ isDetailsOpened ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
      </v-btn>
      <SuggestView
        v-if="!readonly"
        :readonly="readonly"
        :value="addressLocation"
        :error-messages="errorMessages || {}"
        :disabled="isSearchLoading"
        @input="suggestViewSelected"
        @clear-location="clearLocation"
      />
      <div v-else class="d-flex flex-column flex-grow-1">
        <v-text-field
          class="mr-3"
          outlined
          :readonly="readonly"
          hide-details
          :value="addressLocation"
          :loading="loading"
          :disabled="loading"
        />
      </div>
      <v-btn
        small
        elevation="2"
        width="5%"
        height="54px"
        success
        :disabled="!coordsList.length"
        @click="isMapOpened = !isMapOpened"
      >
        <v-icon>mdi-map</v-icon>
      </v-btn>
    </div>
    <slot name="search-bottom" />
    <div v-if="isDetailsOpened" class="d-flex mb-3">
      <v-text-field
        :disabled="loading || isSearchLoading"
        :loading="loading || isSearchLoading"
        :value="displayAddress"
        label="Отображаемый адрес"
        class="mr-3"
        outlined
        hide-details
        @input="changeFieldDisplayAddress"
      />
      <v-text-field
        outlined
        label="Широта"
        class="mr-3"
        :readonly="readonly"
        :disabled="loading || isSearchLoading"
        :loading="loading || isSearchLoading"
        :value="address.lat"
        hide-details
        :error-messages="errorMessages ? getFieldErrorMessage('lat') : []"
        @input="updateLocationPart(Number($event), 'lat')"
      />
      <v-text-field
        outlined
        label="Долгота"
        :readonly="readonly"
        :disabled="loading || isSearchLoading"
        :value="address.lon"
        hide-details
        :error-messages="errorMessages ? getFieldErrorMessage('lon') : []"
        @input="updateLocationPart(Number($event), 'lon')"
      />
    </div>
    <v-row no-gutters class="my-2">
      <v-alert v-if="isError" dense text type="warning">
        Геопозиция точки не найдена
      </v-alert>
    </v-row>
    <div v-if="isMapOpened" style="height: 400px; width: 100%">
      <yandex-map
        v-if="coordsList.length"
        :settings="mapSettings"
        :coords="coordsList"
        :zoom="15"
        @click="onMapClick"
      >
        <ymap-marker :coords="coordsList" :marker-id="0" />
      </yandex-map>
    </div>
  </div>
</template>

<script>
import Geolocation from '@/utils/types/Geolocation'
import { yandexMap, ymapMarker } from 'vue-yandex-maps'
import SuggestView from './SuggestView.vue'
import geo from '@/configs/geo'
import ymapsConfig from '@/configs/ymapsConfig'

export default {
  name: 'LocationAutocomplete',
  props: {
    location: {
      type: Array,
      default: () => geo.defaultCoords
    },
    address: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    errorAutocomplete: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [Object, Array, String],
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  components: {
    SuggestView,
    yandexMap,
    ymapMarker
  },
  computed: {
    coordsList () {
      const { lat, lon } = this.address
      if (lat === null && lon === null) {
        return []
      }
      return Object.values({
        lat,
        lon
      })
    }
  },
  data () {
    return {
      mapSettings: Object.assign({}, ymapsConfig),
      isDetailsOpened: true,
      isMapOpened: false,
      isSearchLoading: false,
      addressLocation: null,
      isError: false,
      displayAddress: null
    }
  },
  watch: {
    address: {
      deep: true,
      handler (newVal) {
        this.updateLocationAddressLocation(newVal)
      }
    },
    errorMessages: {
      deep: true,
      handler (newValue) {
        if (newValue?.lat || newValue?.lon) {
          this.isDetailsOpened = true
        }
      }
    }
  },
  methods: {
    updateCoordsAndLocations (coords) {
      this.updateLocationPart(coords[0], 'lat')
      this.updateLocationPart(coords[1], 'lon')
    },
    onMapClick (e) {
      const coords = e.get('coords')
      this.updateCoordsAndLocations(coords)
      // eslint-disable-next-line no-undef
      ymaps
        .geocode(coords)
        .then(res =>
          this.updateLocationPart(res.geoObjects.get(0).getAddressLine(), 'address')
        )
    },
    updateLocationAddressLocation ({ lat, lon, address, displayAddress }) {
      this.updateCoordsAndLocations([lat, lon])
      this.addressLocation = address
      this.displayAddress = displayAddress || address
    },
    async updateLocationPart (value, type) {
      if (value !== undefined) {
        this.$emit('update-location-part', {
          value: type === 'address' ? value : Number(value),
          fieldName: type
        })
      }
    },
    async suggestViewSelected (event) {
      await fetch(
        `${geo.geocode}/?format=json&geocode=${event}&lang=ru_RU&apikey=${ymapsConfig.apiKey}&suggest_apikey=${ymapsConfig.suggestApiKey}`
      )
        .then(response => response.json())
        .then(res => {
          const geoMember = res.response.GeoObjectCollection.featureMember[0]
          if (geoMember) {
            this.updateCoordsAndLocations(
              geoMember.GeoObject.Point.pos.split(' ').reverse()
            )
            this.addressLocation = event
            this.displayAddress = event
            this.emitLocation()
          }
          this.isError = false
        })
        .catch(() => {
          this.isError = true
        })
    },
    setLocation () {
      // clear location
      if (this.addressLocation === null) {
        this.clearLocation()
        return
      }
      this.emitLocation()
    },
    emitLocation () {
      // set location
      this.$emit(
        'set-location',
        new Geolocation({
          address: this.addressLocation,
          displayAddress: this.displayAddress,
          lat: this.address.lat,
          lon: this.address.lon,
          meta: {
            ymap: this.addressLocation
          }
        })
      )
    },
    changeFieldDisplayAddress (event) {
      this.displayAddress = event
      this.emitLocation()
    },
    clearLocation () {
      this.$emit('set-location', new Geolocation())
    },
    getFieldErrorMessage (fieldName) {
      const msg = this.errorMessages[fieldName]
      if (msg) {
        if (this.address[fieldName]) {
          return []
        }
        return [msg]
      }
      return []
    }
  },
  mounted () {
    const { lat, lon, address, displayAddress } = this.address
    if (lat !== 0 && lon !== 0) {
      this.updateLocationPart(lat, 'lat')
      this.updateLocationPart(lon, 'lon')
    }
    this.addressLocation = address
    this.displayAddress = displayAddress || address
    if (!this.coordsList.length) {
      this.isMapOpened = false
    }
  }
}
</script>

<style lang="css">
.ymap-container {
  height: 100%;
}
</style>
